import { usePage } from '@inertiajs/react'
import { useCallback } from 'react'
import type { Account } from '@/types/shared'

interface Inbox {
  id: string
  slug: string
  name: string
}

interface PageProps {
  current_account?: Account | null
  inbox?: Inbox | null
  [key: string]: unknown
}

interface RouteParams {
  account_id?: string
  inbox_id?: string
  id?: string
  [key: string]: string | undefined
}

/**
 * Hook to help with route parameters by automatically injecting
 * current account and inbox IDs from the page props.
 *
 * Usage:
 * ```tsx
 * import routes from '@/routeHelpers'
 * import { useRouteParams } from '@/hooks/useRoutes'
 *
 * function MyComponent() {
 *   const { withAccount, withInbox } = useRouteParams()
 *
 *   // Just need account slug
 *   const inboxUrl = routes.inbox.show.path(withAccount())
 *
 *   // Need account and inbox
 *   const conversationUrl = routes.conversations.show.path(withInbox({ id: conversation.id }))
 * }
 * ```
 */
export function useRouteParams() {
  const { props } = usePage<PageProps>()
  const { current_account: currentAccount, inbox } = props

  /**
   * Add current account slug to params
   */
  const withAccount = useCallback((params: RouteParams = {}): RouteParams => {
    const mergedParams = { ...params }

    if (!mergedParams.account_id && currentAccount?.slug) {
      mergedParams.account_id = currentAccount.slug
    }

    return mergedParams
  }, [currentAccount?.slug])

  /**
   * Add current account slug and inbox ID to params
   */
  const withInbox = useCallback((params: RouteParams = {}): RouteParams => {
    const mergedParams = { ...params }

    if (!mergedParams.account_id && currentAccount?.slug) {
      mergedParams.account_id = currentAccount.slug
    }

    if (!mergedParams.inbox_id && inbox?.id) {
      mergedParams.inbox_id = inbox.id
    }

    return mergedParams
  }, [currentAccount?.slug, inbox?.id])

  return {
    currentAccount,
    inbox,
    withAccount,
    withInbox,
  }
}
